/**
 * Entity document schemas — compiled-runtime resolvers and pure schema helpers.
 */

import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import { type Extensions, Node } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { SuggestionOptions } from "@tiptap/suggestion";
import { all, createLowlight } from "lowlight";
import { ClarificationAnchorMark } from "../clarification-anchor-mark";
import { DataCatalogEntryNode, type DataCatalogEntryNodeOptions } from "../data-catalog-entry-node";
import { DataCatalogTemplateEntryNode } from "../data-catalog-template-entry-node";
import { DocumentEvidenceNode } from "../document-evidence-node";
import { HighlightMark } from "../highlight-mark";
import type { StorageImageOptions } from "../image-node";
import { StorageImage } from "../image-node";
import { configureMentionNode } from "../mention-node";
import { AtomicField, ParagraphSection, RichSection, SectionTitle } from "../nodes";
import type { FieldUiOptions } from "../nodes/types";
import {
  AttachmentMentionPaste,
  BlockClarification,
  type BlockClarificationOptions,
  BlockIds,
  ExternalMarkdownPaste,
  FirstSectionHeaderSelection,
  MentionTokenPaste,
  SchemaAwareDelete,
  StructuredPaste,
  TabNavigation,
} from "../plugins";
import type { DocumentSchemaConfig } from "./types";

export type { CompiledSchemaRegistry } from "./compiled-template-resolver";
export {
  buildCompiledSchemaRegistry,
  createRuntimeDocumentInitialContent,
  extractRuntimeDocumentFormData,
  getRuntimeTabSchemaIfPresent,
  resolveRuntimeDocumentSchema,
  resolveRuntimeEntityTabMap,
  resolveRuntimeTabSchema,
  resolveRuntimeTemplateVersion,
} from "./compiled-template-resolver";
export { createTabInitialContent, extractTabFormData, hydrateTabDocument } from "./tab-registry";

export type {
  DocumentEntityType,
  DocumentSchemaConfig,
  DocumentTabMap,
  ExtractedFormData,
} from "./types";

type MentionSuggestionConfig = Partial<Omit<SuggestionOptions, "editor">>;
const lowlight = createLowlight(all);

/**
 * Build a Tiptap extensions array from a tab schema config.
 * Active runtime callers should resolve the schema first, then build extensions.
 */
export function createTabDocumentSchema(
  schema: DocumentSchemaConfig,
  mentionSuggestion?: MentionSuggestionConfig,
  blockClarificationOptions?: Partial<BlockClarificationOptions>,
  fieldUiOptions?: FieldUiOptions,
  dataCatalogOptions?: Partial<DataCatalogEntryNodeOptions>,
  imageOptions?: StorageImageOptions
): Extensions {
  return buildExtensionsFromConfig(
    schema,
    mentionSuggestion,
    blockClarificationOptions,
    fieldUiOptions,
    dataCatalogOptions,
    imageOptions
  );
}

function buildExtensionsFromConfig(
  config: DocumentSchemaConfig,
  mentionSuggestion?: MentionSuggestionConfig,
  blockClarificationOptions?: Partial<BlockClarificationOptions>,
  fieldUiOptions?: FieldUiOptions,
  dataCatalogOptions?: Partial<DataCatalogEntryNodeOptions>,
  imageOptions?: StorageImageOptions
): Extensions {
  const DocumentNode = Node.create({
    name: "doc",
    topNode: true,
    content: config.contentExpression,
  });
  return [
    DocumentNode,
    StarterKit.configure({ document: false, codeBlock: false }),
    CodeBlockLowlight.configure({
      lowlight,
      defaultLanguage: "plaintext",
      HTMLAttributes: {
        class: "language-plaintext",
      },
    }),
    StorageImage.configure({
      allowBase64: false,
      resize: {
        enabled: true,
        alwaysPreserveAspectRatio: true,
      },
      HTMLAttributes: { class: "editor-image" },
      ...(imageOptions ?? {}),
    }),
    ClarificationAnchorMark,
    HighlightMark,
    configureMentionNode(mentionSuggestion),
    DocumentEvidenceNode,
    DataCatalogEntryNode.configure(dataCatalogOptions ?? {}),
    DataCatalogTemplateEntryNode.configure(dataCatalogOptions ?? {}),
    SectionTitle.configure({ fieldUiOptions }),
    ParagraphSection.configure({ fieldUiOptions }),
    RichSection.configure({ fieldUiOptions }),
    AtomicField.configure({ fieldUiOptions }),
    BlockIds,
    FirstSectionHeaderSelection,
    SchemaAwareDelete.configure({ schemaConfig: config }),
    BlockClarification.configure(blockClarificationOptions ?? {}),
    TabNavigation,
    StructuredPaste.configure({ schemaConfig: config }),
    ExternalMarkdownPaste,
    MentionTokenPaste,
    AttachmentMentionPaste,
  ];
}
